// Content type by lowercase file extension, used to fill in a usable MIME when
// the browser reports none. Browsers commonly report an EMPTY `File.type` for
// archives (.zip, .7z, .rar) and some office/doc formats; passing that empty
// value straight through makes Storage and Chatwoot fall back to a blank /
// `application/octet-stream` type, which is why e.g. ZIP attachments were
// rejected while CSV/Excel (which carry a proper MIME) went through.
const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  // Archives
  zip: 'application/zip',
  '7z': 'application/x-7z-compressed',
  rar: 'application/vnd.rar',
  tar: 'application/x-tar',
  gz: 'application/gzip',
  // Documents
  pdf: 'application/pdf',
  txt: 'text/plain',
  csv: 'text/csv',
  rtf: 'application/rtf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  xml: 'application/xml',
  json: 'application/json',
  // Images
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  bmp: 'image/bmp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  heic: 'image/heic',
}

// Resolve a usable content type for a file. A specific caller/browser-provided
// MIME is always trusted; only a missing or generic `application/octet-stream`
// value is filled in from the file extension (so .zip -> application/zip, .pdf
// -> application/pdf, …). Callers that pass an explicit MIME (e.g. 'text/csv')
// are therefore unaffected.
export function resolveContentType(fileName: string, fileType: string): string {
  const provided = (fileType || '').trim()
  if (provided && provided.toLowerCase() !== 'application/octet-stream') {
    return provided
  }
  const ext = fileName.match(/\.([a-z0-9]{1,8})$/i)?.[1]?.toLowerCase()
  const mapped = ext ? CONTENT_TYPE_BY_EXTENSION[ext] : undefined
  return mapped ?? (provided || 'application/octet-stream')
}

export function getContentDisposition(
  fileName: string,
  fileType: string
): string {
  // Download all files by default but pdf files.
  const isAttachment = fileType !== 'application/pdf'
  return `${isAttachment ? 'attachment; ' : 'inline; '}filename*=UTF-8''${encodeURIComponent(fileName)}`
}

export function getFileMetadata(
  fileName: string,
  fileType: string
): { contentType: string; contentDisposition: string } {
  const contentType = resolveContentType(fileName, fileType)
  return {
    contentType,
    contentDisposition: getContentDisposition(fileName, contentType),
  }
}

const ENCODED_FILE_NAME_REGEX = /filename\*=UTF-8''([^;]+)/
const QUOTED_FILE_NAME_REGEX = /filename="([^"]+)"/

/**
 * Reads the filename out of a `contentDisposition` header. Understands both the
 * RFC 5987 form written by current uploads and the quoted form on older objects.
 * Returns null when the header carries no filename.
 */
export function parseContentDispositionFileName(
  contentDisposition: string | undefined
): string | null {
  if (!contentDisposition) {
    return null
  }

  const encoded = contentDisposition.match(ENCODED_FILE_NAME_REGEX)
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1])
    } catch {
      return encoded[1]
    }
  }

  return contentDisposition.match(QUOTED_FILE_NAME_REGEX)?.[1] ?? null
}

/**
 * Whether a browser displays this content type usefully on its own.
 *
 * Narrower than `getContentDisposition`, which governs fresh uploads. This
 * governs repairs to stored objects, where forcing a download on an already
 * working preview would be a regression.
 */
export function isBrowserRenderableType(contentType: string): boolean {
  const type = (contentType || '').trim().toLowerCase()
  return type === 'application/pdf' || type.startsWith('image/')
}
